/**
 * Compact pill row of telemetry indicators for a camera.
 * Used on the Events page header when a camera is selected (#038).
 *
 * Rendering rules:
 *   - Pills only show when the underlying field is non-null.
 *   - Battery turns amber when < 25%.
 *   - SD-percent-used pill turns amber when > 90%.
 *   - Otherwise pills use the muted-foreground style consistent with the
 *     rest of the dashboard.
 */

import type { LucideIcon } from 'lucide-react'
import { Battery, Signal, Thermometer, HardDrive } from 'lucide-react'

import { cn, formatSDCard } from '@/lib/utils'
import type { Camera } from '@/types'

function Pill({
  icon: Icon,
  label,
  tone = 'default',
}: {
  icon: LucideIcon
  label: string
  tone?: 'default' | 'warn'
}) {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
        tone === 'default' && 'bg-muted/40 text-muted-foreground',
        tone === 'warn' && 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
      )}
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span>{label}</span>
    </div>
  )
}

export function CameraTelemetryPills({ camera }: { camera: Camera }) {
  const batteryLow = camera.battery_level !== null && camera.battery_level < 25

  const sdPctUsed =
    camera.sd_total_mb && camera.sd_total_mb > 0 && camera.sd_used_mb !== null
      ? Math.round((camera.sd_used_mb / camera.sd_total_mb) * 100)
      : null
  const sdRemainingMb =
    camera.sd_total_mb !== null && camera.sd_used_mb !== null
      ? Math.max(camera.sd_total_mb - camera.sd_used_mb, 0)
      : null
  const sdUsedFormatted = formatSDCard(camera.sd_used_mb, camera.sd_total_mb)
  const sdRemainingFormatted =
    sdRemainingMb !== null && camera.sd_total_mb !== null
      ? formatSDCard(sdRemainingMb, camera.sd_total_mb)?.split('/')[0]
      : null

  return (
    <div className="flex flex-wrap items-center gap-2">
      {camera.battery_level !== null && (
        <Pill
          icon={Battery}
          label={`${camera.battery_level}%`}
          tone={batteryLow ? 'warn' : 'default'}
        />
      )}
      {camera.signal_level !== null && (
        <Pill icon={Signal} label={`${camera.signal_level}/5`} />
      )}
      {camera.temperature !== null && (
        <Pill icon={Thermometer} label={`${camera.temperature}°C`} />
      )}
      {sdUsedFormatted && (
        <Pill icon={HardDrive} label={sdUsedFormatted} />
      )}
      {sdRemainingFormatted && sdPctUsed !== null && (
        <Pill
          icon={HardDrive}
          label={`${sdRemainingFormatted} free (${100 - sdPctUsed}%)`}
          tone={sdPctUsed > 90 ? 'warn' : 'default'}
        />
      )}
    </div>
  )
}
